import { useEffect, createContext, useState, useRef } from "react";
import { getAuth, getConfig } from "./api_client/allauth";
import { sendExtensionMessage } from "./api_client/allauth";
import { authStatusSync, notifyLoginSuccess, notifyLogout } from "@sharpana/shared/utils/webViewBridge";
import * as Sentry from "@sentry/react";
import { LoadingCircle } from "@sharpana/ui/LoadingCircle";

export const AuthContext = createContext<any>(null);

function LoadingError() {
  return <div>Loading error!</div>;
}

// Provide a compact, safe summary of the auth change event for Sentry
function summarizeAuthEvent(evt: any) {
  try {
    const d = evt?.detail ?? null;
    const status = d?.status ?? null;
    const isAuthenticated =
      d?.meta?.is_authenticated === true || !!(d?.data?.user || d?.user);
    const hasUser = !!(d?.data?.user || d?.user);
    let hasPendingFlow = false;
    try {
      hasPendingFlow = Array.isArray(d?.data?.flows)
        ? d!.data!.flows!.some((f: any) => !!f?.is_pending)
        : false;
    } catch (_) {
      hasPendingFlow = false;
    }
    const u = d?.data?.user ?? d?.user ?? null;
    const userId = u?.id ?? null;
    const pId = u?.p_id ?? null;
    const isWebView = !!(window as any)?.ReactNativeWebView;
    return {
      type: String(evt?.type || ""),
      status,
      isAuthenticated,
      hasUser,
      hasPendingFlow,
      hasMeta: !!d?.meta,
      hasData: !!d?.data,
      userId,
      pId,
      isWebView,
    };
  } catch (_) {
    return { type: String(evt?.type || "") };
  }
}

export function AuthContextProvider(props: any) {
  const [auth, setAuth] = useState<any | undefined>(undefined);
  const [config, setConfig] = useState<any | undefined>(undefined);
  const prevAuthRef = useRef<any | undefined>(undefined);
  const loginFallbackScheduledRef = useRef<boolean>(false);

  useEffect(() => {
    async function onAuthChanged(e: any) {
      setAuth((auth: any) => {
        if (typeof auth === "undefined") {
          console.log("Authentication status loaded");
        } else {
          console.log("Authentication status updated");
        }
        return e.detail;
      });

      // Centralized login/logout notifications for RN WebView
      try {
        const prev = prevAuthRef.current;
        const next = e.detail;
        const wasAuthenticated = !!(
          prev && (prev.meta?.is_authenticated === true || prev?.data?.user)
        );
        const isAuthenticated = !!(
          next && (next.meta?.is_authenticated === true || next?.data?.user)
        );

        if (!wasAuthenticated && isAuthenticated) {
          // Transition: logged in -> hydrate and notify once
          const fetchSessionWithRetry = async (attempts = 3, delayMs = 50) => {
            for (let i = 0; i < attempts; i++) {
              try {
                const sess = await getAuth();
                const hydratedUser = sess?.data?.user || sess?.user || null;
                if (hydratedUser) return hydratedUser;
              } catch (_) {}
              await new Promise((r) => setTimeout(r, delayMs));
            }
            return null;
          };
          try {
            // Give cookies a moment to settle
            await new Promise((r) => setTimeout(r, 50));
            const hydratedUser = await fetchSessionWithRetry(3, 50);
            if (hydratedUser) {
              notifyLoginSuccess(hydratedUser);
              // Reset fallback guard on success so future cycles can schedule if needed
              loginFallbackScheduledRef.current = false;
            } else if (!loginFallbackScheduledRef.current) {
              // One-time delayed fallback attempt
              loginFallbackScheduledRef.current = true;
              setTimeout(async () => {
                try {
                  const sess = await getAuth();
                  const u = sess?.data?.user || sess?.user || null;
                  if (u) {
                    notifyLoginSuccess(u);
                  } else {
                    console.warn("[AuthContext] Fallback hydration still returned null user; skipping notify");
                    Sentry.captureMessage(
                      "[AuthContext] Fallback hydration returned null user after delayed retry",
                      {
                        level: "warning",
                        tags: { area: "auth", phase: "fallback_hydration_1" },
                        extra: {
                          location: "AuthContext.onAuthChanged",
                          href: window?.location?.href,
                          event: summarizeAuthEvent(e),
                        },
                      }
                    );
                  }
                } catch (err) {
                  console.warn("[AuthContext] Fallback hydration failed", err);
                  Sentry.captureException(err, {
                    tags: { area: "auth", phase: "fallback_hydration_2" },
                    extra: {
                      location: "AuthContext.onAuthChanged",
                      href: window?.location?.href,
                      event: summarizeAuthEvent(e),
                    },
                  });
                }
              }, 300);
            }
          } catch (err) {
            console.warn("[AuthContext] Session hydration/notify failed", err);
            Sentry.captureException(err, {
              tags: { area: "auth", phase: "hydrate_notify" },
              extra: {
                location: "AuthContext.onAuthChanged",
                href: window?.location?.href,
                event: summarizeAuthEvent(e),
              },
            });
          }
        } else if (wasAuthenticated && !isAuthenticated) {
          // Transition: logged out
          try {
            notifyLogout();
            // Reset fallback flag on logout to allow scheduling in next login cycle
            loginFallbackScheduledRef.current = false;
          } catch (err) {
            console.warn("[AuthContext] notifyLogout failed", err);
            Sentry.captureException(err, {
              tags: { area: "auth", phase: "notify_logout" },
              extra: {
                location: "AuthContext.onAuthChanged",
                href: window?.location?.href,
                event: summarizeAuthEvent(e),
              },
            });
          }
        }

        prevAuthRef.current = next;
      } catch (err) {
        console.warn("[AuthContext] Transition detection failed", err);
        Sentry.captureException(err, {
          tags: { area: "auth", phase: "transition_detection" },
          extra: {
            location: "AuthContext.onAuthChanged",
            href: window?.location?.href,
            event: summarizeAuthEvent(e),
            has_prev_ref: !!prevAuthRef.current,
          },
        });
      }

      // TODO: Propagate via BroadcastChannel as well (to reach non-extension/non-iframed instances)

      // Propagte to extension instances
      const editorExtensionId = import.meta.env.VITE_CHROME_EXTENSION_ID;

      try {
        // if chrome extension runtime available, trigger auth propagation event
        // console.log("Web app will start contacting extension");
        const response = await sendExtensionMessage(editorExtensionId, {
          message: "refresh_auth_state",
          data: e.detail,
        });
        console.log(
          "Web app received response from extension (on AuthContext.tsx)"
        );
        console.log(response);
        // console.log("Web app finished contacting extension");
      } catch (error) {
        console.log("Chrome extension runtime not found...");
      }
    }

    window.addEventListener(
      "message",
      (e: MessageEvent) => {
        // TODO: Validate this message comes from a trusted origin
        // This refresh_auth message can come from the web extension contentscript iframe parent, in order to receive the auth propagation event
        if (e.data.message === "refresh_auth") {
          // console.log('Auth has been refreshed...')
          setAuth(e.data.data);
        }
      },
      false
    );

    document.addEventListener("allauth.auth.change", onAuthChanged);
    getAuth()
      .then((data) => {
        setAuth(data);
        prevAuthRef.current = data;
        authStatusSync(data); // Called here to re-sync after mobile app killed/suspended with user logged in and then re-activated
      })
      .catch((e) => {
        console.error(e);
        setAuth(false);
        Sentry.captureException(e);
      });
    getConfig()
      .then((data) => setConfig(data))
      .catch((e) => {
        console.error(e);
      });
    return () => {
      document.removeEventListener("allauth.auth.change", onAuthChanged);

      // window.removeEventListener(
      //   "message",
      //   (e: MessageEvent) => {
      //     console.log(`message received: ${e.data}`);
      //   },
      //   false
      // );
    };
  }, []);
  const loading = typeof auth === "undefined" || config?.status !== 200;
  return (
    <AuthContext.Provider value={{ auth, config }}>
      {loading ? (
        <LoadingCircle />
      ) : auth === false ? (
        <LoadingError />
      ) : (
        props.children
      )}
    </AuthContext.Provider>
  );
}
